// D-SS10 (17/09/2026) — Misty's own label for "this browser on this computer".
//
// Why a label we make up ourselves
// --------------------------------
// The report needs to answer "is this the same computer?", and the honest
// answer is that a web browser will not tell any website its MAC address, its
// serial number, its computer name, or who is logged in to it. It never has and
// it never will. What a website genuinely sees is the address the connection
// came from — which identifies the CONNECTION, not the computer: a whole office
// shares one, and most home and mobile connections change theirs every few days
// — plus whatever the browser volunteers about itself.
//
// So Misty generates a random value the first time a browser opens the login
// page and sends it back with every sign-in attempt from then on. That is the
// one field that says "the same computer that failed five times yesterday just
// succeeded", even though the address changed.
//
// What it is not: proof of identity, and not tamper-proof. Clearing site data,
// a private window or a different browser on the same machine all produce a new
// label, and somebody technical can change it deliberately. It is a useful
// label on ordinary traffic, not a security control, and the report reads a
// blank one as "not recorded", never as "unknown device".
//
// Callum's answer to Q1 (option (a), 17/09/2026): the cookie is called
// `misty_device`, it lasts two years, and it is refreshed on every visit so a
// computer in regular use keeps its label indefinitely.

export const DEVICE_COOKIE = 'misty_device';
export const DEVICE_STORAGE_KEY = 'misty_device';
export const DEVICE_COOKIE_DAYS = 730; // two years (Q1, option (a))

const readCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch (_e) {
    return '';
  }
};

const writeCookie = (name, value, days) => {
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // `Secure` only on https. Misty is still served over plain http inside the
    // office (D-SEC1 is open), and a browser silently DROPS a Secure cookie on
    // an http page — the label would never stick where it is needed most.
    const secure = (typeof window !== 'undefined'
      && window.location && window.location.protocol === 'https:') ? '; Secure' : '';
    document.cookie =
      `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${secure}`;
  } catch (_e) { /* a browser refusing cookies must never break the login */ }
};

const newId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_e) { /* fall through */ }
  // Only reached on an old browser with no crypto.randomUUID. Still random
  // enough to tell two office computers apart, which is all this does.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

// Kept in TWO places on purpose. Browsers clear site data more eagerly than
// they expire cookies, and a cookie can be blocked where localStorage is not,
// so whichever survives puts the other one back.
export const getDeviceId = () => {
  let fromStore = '';
  try { fromStore = localStorage.getItem(DEVICE_STORAGE_KEY) || ''; } catch (_e) { /* ignore */ }
  const fromCookie = readCookie(DEVICE_COOKIE);

  const id = fromStore || fromCookie || newId();

  if (id !== fromStore) {
    try { localStorage.setItem(DEVICE_STORAGE_KEY, id); } catch (_e) { /* ignore */ }
  }
  // Written on every call, which is what "refreshed on each visit" means: the
  // two years start again from today, so a computer in use never loses its
  // label.
  writeCookie(DEVICE_COOKIE, id, DEVICE_COOKIE_DAYS);

  return id;
};

// What the login page volunteers about the computer, as headers. Headers rather
// than body fields so the staff login, the two code screens and the customer
// portal login — three different request shapes — all carry them the same way,
// and no request model had to change to accept them.
export const deviceHeaders = () => {
  const headers = { 'X-Misty-Device': getDeviceId() };
  try {
    if (typeof window !== 'undefined' && window.screen) {
      headers['X-Misty-Screen'] = `${window.screen.width}x${window.screen.height}`;
    }
  } catch (_e) { /* ignore */ }
  try {
    if (typeof navigator !== 'undefined' && navigator.language) {
      headers['X-Misty-Language'] = navigator.language;
    }
  } catch (_e) { /* ignore */ }
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone) headers['X-Misty-Timezone'] = zone;
  } catch (_e) { /* ignore */ }
  return headers;
};

// The four routes that are a sign-in attempt. Everything else is left alone:
// there is no reason for the device label to travel on every request in the
// program, and it is not used for anything but this report.
const LOGIN_PATHS = [
  '/auth/login',
  '/auth/2fa/verify',
  '/auth/2fa/verify-recovery',
  '/customer-portal/auth/login',
];

export const isLoginRequest = (url) => {
  const path = String(url || '');
  return LOGIN_PATHS.some((candidate) => path.includes(candidate));
};
